<script setup>
/**
 * Who can see this case.
 *
 * The verdict is computed once on the server by the permission scope; this
 * badge only renders it. An unrecognised mode falls back to the most
 * restrictive label, so the badge can never overstate the audience.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import { VISIBILITY, visibilityMeta } from 'dashboard/helper/supportCaseMeta';

const props = defineProps({
  mode: {
    type: String,
    default: VISIBILITY.PRIVATE,
  },
  // Named teams for the `teams` mode; the badge shows them so an agent can see
  // at a glance which teams a shared case reaches.
  teamNames: {
    type: Array,
    default: () => [],
  },
  compact: {
    type: Boolean,
    default: true,
  },
});

const { t } = useI18n();

const meta = computed(() => visibilityMeta(props.mode));

const title = computed(() => {
  if (props.mode === VISIBILITY.TEAMS && props.teamNames.length) {
    return props.teamNames.join(', ');
  }
  return t(meta.value.labelKey);
});

const label = computed(() => ({
  title: title.value,
  description: t(meta.value.descriptionKey),
}));
</script>

<template>
  <Label
    :label="label"
    color="slate"
    :compact="compact"
    data-testid="visibility-badge"
  >
    <template #icon>
      <Icon :icon="meta.icon" class="flex-shrink-0 size-3 text-n-slate-11" />
    </template>
  </Label>
</template>
